import * as amplitude from "@amplitude/analytics-browser"
import { Identify } from "@amplitude/analytics-browser"
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser"

type SessionReplayPluginOptions = NonNullable<Parameters<typeof sessionReplayPlugin>[0]>

interface IdentifyAmplitudeUserInput {
  email?: string | null
  id: string
  username?: string | null
}

const CHECKOUT_REPLAY_FLOW_KEY = "amplitudeCheckoutReplayFlowStarted"
const CHECKOUT_REPLAY_FLOW_MAX_AGE_MS = 2 * 60 * 60 * 1000
const CHECKOUT_REPLAY_RETURN_EVENTS = new Set(["CheckoutPaymentSuccess", "CheckoutPaymentFailed"])
const CHECKOUT_REPLAY_START_PATH_PATTERN = /^\/c\/[^/]+\/checkout\/?$/
const CHECKOUT_REPLAY_SUBSCRIBE_PATH_PATTERN = /^\/c\/[^/]+\/checkout-subscribe\/?$/
const CHECKOUT_REPLAY_RETURN_PATH_PATTERN = /^\/c\/[^/]+\/checkout-return\/?$/

let amplitudeInitPromise: Promise<void> | undefined
let sessionReplayEnabled = false
let sessionReplayTracking: ReturnType<typeof sessionReplayPlugin> | undefined
let sessionReplayQueue: Promise<void> = Promise.resolve()

const isBrowser = () => typeof window !== "undefined"

const getUrlParts = (url: string) => {
  try {
    const parsedUrl = new URL(url)

    return {
      pathname: parsedUrl.pathname,
      searchParams: parsedUrl.searchParams,
    }
  } catch {
    const pathAndSearch = url.split("#")[0] ?? ""
    const searchStartIndex = pathAndSearch.indexOf("?")
    const pathname =
      searchStartIndex === -1 ? pathAndSearch : pathAndSearch.slice(0, searchStartIndex)
    const search = searchStartIndex === -1 ? "" : pathAndSearch.slice(searchStartIndex + 1)

    return {
      pathname,
      searchParams: new URLSearchParams(search),
    }
  }
}

const getAmplitudeEnvironment = () =>
  process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.VERCEL_ENV || process.env.NODE_ENV

const isAmplitudeAnalyticsEnabled = () => {
  const environment = getAmplitudeEnvironment()

  return environment === "staging" || environment === "production"
}

const isAmplitudeSessionReplayEnabled = () => getAmplitudeEnvironment() === "production"

const getCheckoutReplayUrlState = (url: string) => {
  const { pathname, searchParams } = getUrlParts(url)
  const isReturn = CHECKOUT_REPLAY_RETURN_PATH_PATTERN.test(pathname)

  return {
    isCheckout: pathname.includes("/checkout"),
    isReturn,
    isReturnWithIds: isReturn && (searchParams.has("purchaseIds") || searchParams.has("order_id")),
    isStart:
      CHECKOUT_REPLAY_START_PATH_PATTERN.test(pathname) ||
      CHECKOUT_REPLAY_SUBSCRIBE_PATH_PATTERN.test(pathname),
  }
}

const isCheckoutReplayStartPage = (pathname: string) =>
  pathname === "/c/[code]/checkout" || pathname === "/c/[code]/checkout-subscribe"

// The live browser URL, which (unlike router.asPath) always carries the query string —
// even on the first render of an auto-exported dynamic route like checkout-return.
const getCurrentCheckoutReplayUrl = () =>
  isBrowser() ? `${window.location.pathname}${window.location.search}` : ""

const getCheckoutReplayStorages = () =>
  isBrowser() ? [window.sessionStorage, window.localStorage] : []

const writeCheckoutReplayFlowMarker = (value?: string) => {
  for (const storage of getCheckoutReplayStorages()) {
    try {
      if (value) {
        storage.setItem(CHECKOUT_REPLAY_FLOW_KEY, value)
      } else {
        storage.removeItem(CHECKOUT_REPLAY_FLOW_KEY)
      }
    } catch {
      // Storage can be unavailable in private browsing or strict browser modes.
    }
  }
}

const readCheckoutReplayFlowMarker = () => {
  for (const storage of getCheckoutReplayStorages()) {
    try {
      const value = storage.getItem(CHECKOUT_REPLAY_FLOW_KEY)
      if (value) return value
    } catch {
      // Storage can be unavailable in private browsing or strict browser modes.
    }
  }

  return undefined
}

const getCheckoutReplayFlowStartedAt = () => {
  const storedValue = readCheckoutReplayFlowMarker()

  if (!storedValue) return undefined

  const startedAt = storedValue === "true" ? Date.now() : Number(storedValue)
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > CHECKOUT_REPLAY_FLOW_MAX_AGE_MS) {
    writeCheckoutReplayFlowMarker()
    return undefined
  }

  return startedAt
}

const hasActiveCheckoutReplayFlow = () => getCheckoutReplayFlowStartedAt() !== undefined

const createSessionReplayPlugin = () => {
  const options: SessionReplayPluginOptions = {
    deviceId: amplitude.getDeviceId(),
    privacyConfig: {
      blockSelector: ["[data-amp-block]", ".amp-block"],
      defaultMaskLevel: "medium",
      maskSelector: ["[data-amp-mask]", ".amp-mask", ".checkout-sensitive"],
    },
    sampleRate: 1,
    useWebWorker: true,
  }

  return sessionReplayPlugin(options)
}

const queueSessionReplayUpdate = (update: () => Promise<void>) => {
  sessionReplayQueue = sessionReplayQueue.then(update).catch((error: unknown) => {
    console.error("Amplitude session replay update failed", error)
  })

  return sessionReplayQueue
}

const initAmplitude = () => {
  if (!process.env.NEXT_PUBLIC_AMPLITUDE_KEY || !isAmplitudeAnalyticsEnabled()) {
    return Promise.resolve()
  }

  if (amplitudeInitPromise) return amplitudeInitPromise

  amplitudeInitPromise = amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY, undefined, {
    defaultTracking: {
      // Attribution tracking - captures UTM params and referrer
      attribution: {
        excludeReferrers: [], // Don't exclude any referrers
        initialEmptyValue: "EMPTY", // Value when param is missing
        resetSessionOnNewCampaign: true, // New session when UTM changes
      },
      fileDownloads: false,
      formInteractions: false,
      pageViews: true, // Track page navigations for user journey analysis
      sessions: true, // Track sessions for attribution
    },
  }).promise

  return amplitudeInitPromise
}

const enableCheckoutSessionReplay = async () => {
  if (
    !process.env.NEXT_PUBLIC_AMPLITUDE_KEY ||
    !isAmplitudeSessionReplayEnabled() ||
    sessionReplayEnabled
  ) {
    return sessionReplayQueue
  }

  await initAmplitude()

  const nextSessionReplayTracking = createSessionReplayPlugin()
  sessionReplayEnabled = true
  sessionReplayTracking = nextSessionReplayTracking

  return queueSessionReplayUpdate(async () => {
    if (!sessionReplayEnabled || sessionReplayTracking !== nextSessionReplayTracking) return

    await amplitude.add(nextSessionReplayTracking).promise
  })
}

const disableCheckoutSessionReplay = () => {
  if (!sessionReplayEnabled && !sessionReplayTracking) return sessionReplayQueue

  const pluginName = sessionReplayTracking?.name
  sessionReplayEnabled = false
  sessionReplayTracking = undefined

  if (!pluginName) return sessionReplayQueue

  return queueSessionReplayUpdate(async () => {
    await amplitude.remove(pluginName).promise
  })
}

const startCheckoutReplayFlow = async () => {
  if (!isBrowser()) return

  writeCheckoutReplayFlowMarker(`${Date.now()}`)
  await enableCheckoutSessionReplay()
}

const stopCheckoutReplayFlow = ({ clearFlowMarker = true } = {}) => {
  if (clearFlowMarker) writeCheckoutReplayFlowMarker()

  return disableCheckoutSessionReplay()
}

const prepareCheckoutReplayForUrl = (url: string) => {
  if (!isAmplitudeSessionReplayEnabled()) return sessionReplayQueue
  if (!getCheckoutReplayUrlState(url).isStart) return sessionReplayQueue

  return startCheckoutReplayFlow()
}

const syncCheckoutReplayForRoute = (pathname: string, asPath: string) => {
  if (!isBrowser()) return sessionReplayQueue
  if (!isAmplitudeSessionReplayEnabled()) return stopCheckoutReplayFlow()

  // Start pages key on the route pattern, which is always populated (even pre-hydration),
  // so checkout-open capture stays reliable.
  if (isCheckoutReplayStartPage(pathname)) {
    return startCheckoutReplayFlow()
  }

  // Read the live browser URL rather than router.asPath: checkout-return is an
  // auto-exported dynamic route (no getServerSideProps), so on the first render Next
  // hands React a de-optimized asPath without the ?purchaseIds query string.
  const replayUrl = getCheckoutReplayUrlState(getCurrentCheckoutReplayUrl() || asPath)

  // The return URL always carries purchaseIds/order_id (backend-guaranteed for every
  // payment processor) and survives the external payment round-trip, so it alone resumes
  // replay — no Web-Storage marker, which in-app WebViews can evict even though the
  // Amplitude identity cookie survives.
  if (replayUrl.isReturnWithIds) {
    return enableCheckoutSessionReplay()
  }

  // Still inside the checkout flow: keep recording while the flow marker is live, but
  // never clear it here — a transient render must not disarm the return capture.
  if (replayUrl.isCheckout) {
    return hasActiveCheckoutReplayFlow()
      ? enableCheckoutSessionReplay()
      : stopCheckoutReplayFlow({ clearFlowMarker: false })
  }

  // Outside checkout entirely: stop recording and clear the flow marker.
  return stopCheckoutReplayFlow()
}

const shouldStartCheckoutReplayForCurrentPath = (eventType: string) => {
  if (!isBrowser()) return false

  const replayUrl = getCheckoutReplayUrlState(getCurrentCheckoutReplayUrl())

  if (eventType === "CheckoutOpened") {
    return replayUrl.isStart
  }

  // Return events (CheckoutPaymentSuccess/Failed) self-authorize from the URL ids, which
  // survive the external payment round-trip even when the Web-Storage marker does not.
  return CHECKOUT_REPLAY_RETURN_EVENTS.has(eventType) && replayUrl.isReturnWithIds
}

const trackAmplitudeEvent = async (eventType: string, eventProperties: Record<string, unknown>) => {
  if (!isAmplitudeAnalyticsEnabled()) return

  if (shouldStartCheckoutReplayForCurrentPath(eventType)) {
    await startCheckoutReplayFlow()
  } else {
    await initAmplitude()
  }

  await amplitude.track(eventType, eventProperties).promise
}

const DEVICE_ID_WAIT_MS = 2000

// Bridge key for WERZ activity: imported Amplitude history is keyed on this device ID. Only WERZ
// delivery waits for it, briefly, after events are already queued; capture never waits.
const waitForAmplitudeDeviceId = async () => {
  if (!isAmplitudeAnalyticsEnabled()) return undefined
  await Promise.race([
    initAmplitude(),
    new Promise((resolve) => setTimeout(resolve, DEVICE_ID_WAIT_MS)),
  ])
  return amplitude.getDeviceId()
}

const identifyAmplitudeUser = async ({ email, id, username }: IdentifyAmplitudeUserInput) => {
  if (!isAmplitudeAnalyticsEnabled()) return

  await initAmplitude()

  const event = new Identify()
  amplitude.setUserId(id)
  email && event.set("email", email)
  username && event.set("username", username)
  await amplitude.identify(event).promise
}

const resetAmplitude = () => {
  void disableCheckoutSessionReplay()
  amplitude.reset()
}

export {
  initAmplitude,
  identifyAmplitudeUser,
  prepareCheckoutReplayForUrl,
  resetAmplitude,
  stopCheckoutReplayFlow,
  syncCheckoutReplayForRoute,
  trackAmplitudeEvent,
  waitForAmplitudeDeviceId,
}
